import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProducts, getCategories } from '../api'
import { useCart } from '../context/CartContext'
import { useTelegram } from '../context/TelegramContext'
import Footer from '../components/Footer'
import './Catalog.css'

const FALLBACK_CATEGORIES = [
  { id: 'all', name: 'Все', icon: '🛍️' },
  { id: 'Одежда', name: 'Одежда', icon: '👕' },
  { id: 'Куртки', name: 'Куртки', icon: '🧥' },
  { id: 'Жилетки', name: 'Жилетки', icon: '🦺' },
  { id: 'Комбинезоны', name: 'Комбинезоны', icon: '🥋' }
]

const Catalog = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES)
  const [categoriesLoading, setCategoriesLoading] = useState(true)
  const [categoriesError, setCategoriesError] = useState(null)
  
  // Состояния для фильтров и панелей
  const [showFilters, setShowFilters] = useState(false)
  const [showCategoriesList, setShowCategoriesList] = useState(false)
  const [filters, setFilters] = useState({
    colors: [],
    sizes: [],
    priceRange: { min: 0, max: 100000 }
  })
  const [selectedFilters, setSelectedFilters] = useState({
    colors: [],
    sizes: [],
    minPrice: 0,
    maxPrice: 100000
  })
  
  const navigate = useNavigate()
  const { getCartItemsCount, hapticFeedback, addToCart } = useCart()
  const { hideMainButton, isAdmin, showAlert, user } = useTelegram()
  const canManageCategories = isAdmin === true

  // Логирование для отладки
  useEffect(() => {
    console.log('🔍 Проверка прав администратора:')
    console.log('  - User:', user)
    console.log('  - isAdmin:', isAdmin)
    console.log('  - canManageCategories:', canManageCategories)
  }, [user, isAdmin, canManageCategories])

  useEffect(() => {
    loadProducts()
    loadCategories()
    hideMainButton()
  }, [])

  // Извлекаем доступные цвета и размеры из товаров
  useEffect(() => {
    if (products.length > 0) {
      const allColors = new Set()
      const allSizes = new Set()
      let minPrice = Infinity
      let maxPrice = 0

      products.forEach(product => {
        if (product.colors) {
          const colors = typeof product.colors === 'string' ? JSON.parse(product.colors) : product.colors
          colors?.forEach(color => allColors.add(color))
        }
        if (product.sizes) {
          const sizes = typeof product.sizes === 'string' ? JSON.parse(product.sizes) : product.sizes
          sizes?.forEach(size => allSizes.add(size))
        }
        if (product.price < minPrice) minPrice = product.price
        if (product.price > maxPrice) maxPrice = product.price
      })

      setFilters({
        colors: Array.from(allColors),
        sizes: Array.from(allSizes),
        priceRange: { min: Math.floor(minPrice), max: Math.ceil(maxPrice) }
      })
      
      setSelectedFilters(prev => ({
        ...prev,
        minPrice: Math.floor(minPrice),
        maxPrice: Math.ceil(maxPrice)
      }))
    }
  }, [products])

  const loadProducts = async () => {
    try {
      setLoading(true)
      const data = await getProducts()
      setProducts(data)
      setError(null)
    } catch (err) {
      console.error('Ошибка загрузки товаров:', err)
      setError('Не удалось загрузить товары')
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true)
      const data = await getCategories()
      if (data.success && Array.isArray(data.categories)) {
        const mapped = [
          { id: 'all', name: 'Все', icon: '🛍️' },
          ...data.categories.map(cat => ({
            id: cat.name,
            name: cat.name,
            icon: cat.emoji || '📦'
          }))
        ]
        setCategories(mapped)
        setCategoriesError(null)
      } else {
        setCategoriesError('Не удалось загрузить категории')
      }
    } catch (err) {
      console.error('Ошибка загрузки категорий:', err)
      setCategoriesError('Не удалось загрузить категории')
    } finally {
      setCategoriesLoading(false)
    }
  }

  const formatPrice = (value) => {
    if (value === null || value === undefined) return '—'
    return new Intl.NumberFormat('ru-RU').format(value) + ' ₽'
  }

  const getProductImage = (product) => {
    if (product.image_url) {
      const fixedPath = product.image_url.replace(/\\/g, '/')
      return fixedPath.startsWith('/') ? fixedPath : `/${fixedPath}`
    }
    if (product.image_urls) {
      try {
        const urls = typeof product.image_urls === 'string'
          ? JSON.parse(product.image_urls)
          : product.image_urls
        if (Array.isArray(urls) && urls.length > 0) {
          const fixedPath = urls[0].replace(/\\/g, '/')
          return fixedPath.startsWith('/') ? fixedPath : `/${fixedPath}`
        }
      } catch (err) {
        console.warn('Ошибка парсинга image_urls', err)
      }
    }
    return '/placeholder.jpg'
  }

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      // Фильтр по категории
      const categoryMatch = selectedCategory === 'all' || p.category === selectedCategory
      
      // Фильтр по поиску
      const searchMatch = searchQuery === '' || 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(searchQuery.toLowerCase()))
      
      // Фильтр по цвету
      let colorMatch = true
      if (selectedFilters.colors.length > 0) {
        const productColors = typeof p.colors === 'string' ? JSON.parse(p.colors) : p.colors
        colorMatch = productColors?.some(color => selectedFilters.colors.includes(color))
      }
      
      // Фильтр по размеру
      let sizeMatch = true
      if (selectedFilters.sizes.length > 0) {
        const productSizes = typeof p.sizes === 'string' ? JSON.parse(p.sizes) : p.sizes
        sizeMatch = productSizes?.some(size => selectedFilters.sizes.includes(size))
      }
      
      // Фильтр по цене
      const priceMatch = p.price >= selectedFilters.minPrice && p.price <= selectedFilters.maxPrice
      
      return categoryMatch && searchMatch && colorMatch && sizeMatch && priceMatch
    })
  }, [products, selectedCategory, searchQuery, selectedFilters])

  const handleProductClick = (productId) => {
    hapticFeedback('light')
    navigate(`/product/${productId}`)
  }

  const handleCartClick = () => {
    hapticFeedback('medium')
    navigate('/cart')
  }

  const handleProfileClick = () => {
    hapticFeedback('medium')
    navigate('/profile')
  }

  const handleCategoriesClick = () => {
    if (!canManageCategories) {
      showAlert?.('Раздел доступен только администраторам')
      return
    }
    hapticFeedback('medium')
    navigate('/admin/categories')
  }

  const toggleFilters = () => {
    hapticFeedback('light')
    setShowFilters(!showFilters)
  }

  const toggleCategoriesList = () => {
    hapticFeedback('light')
    setShowCategoriesList(!showCategoriesList)
  }

  const handleFilterChange = (type, value) => {
    setSelectedFilters(prev => {
      if (type === 'color') {
        const colors = prev.colors.includes(value)
          ? prev.colors.filter(c => c !== value)
          : [...prev.colors, value]
        return { ...prev, colors }
      }
      if (type === 'size') {
        const sizes = prev.sizes.includes(value)
          ? prev.sizes.filter(s => s !== value)
          : [...prev.sizes, value]
        return { ...prev, sizes }
      }
      if (type === 'minPrice' || type === 'maxPrice') {
        return { ...prev, [type]: Number(value) }
      }
      return prev
    })
  }

  const resetFilters = () => {
    setSelectedFilters({
      colors: [],
      sizes: [],
      minPrice: filters.priceRange.min,
      maxPrice: filters.priceRange.max
    })
    hapticFeedback('light')
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (selectedFilters.colors.length > 0) count++
    if (selectedFilters.sizes.length > 0) count++
    if (selectedFilters.minPrice !== filters.priceRange.min || 
        selectedFilters.maxPrice !== filters.priceRange.max) count++
    return count
  }

  if (loading) {
    return (
      <div className="catalog-loading">
        <div className="spinner"></div>
        <p>Загрузка товаров...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="catalog-error">
        <div className="error-icon">❌</div>
        <h3>Ошибка</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadProducts}>
          Попробовать снова
        </button>
      </div>
    )
  }

  return (
    <div className="catalog">
      {/* Top Bar */}
      <div className="top-bar">
        <div className="top-logo">
          <div className="logo-icon">🏋️‍♂️</div>
          <div className="logo-text">
            <p className="logo-title">meta wer shop</p>
            <p className="logo-subtitle">магазин одежды</p>
          </div>
        </div>
        <button className="top-channel-btn">
          <span>Наш канал</span>
        </button>
      </div>

      {/* Action buttons */}
      <div className="quick-actions">
        <button className="quick-action" onClick={handleProfileClick}>
          <span>📦</span> Мои заказы
        </button>
        <button className="quick-action" onClick={handleCartClick}>
          <span>🛒</span> Корзина
          {getCartItemsCount() > 0 && (
            <span className="quick-action-badge">{getCartItemsCount()}</span>
          )}
        </button>
      </div>

      {/* Search + filters row */}
      <div className="search-row">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input 
            type="text"
            className="search-input"
            placeholder="Я ищу..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button 
              className="search-clear"
              onClick={() => setSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>
        <button className="filter-btn" onClick={toggleFilters}>
          ⚙️
          {getActiveFiltersCount() > 0 && (
            <span className="filter-badge">{getActiveFiltersCount()}</span>
          )}
        </button>
        <button className="view-btn" onClick={toggleCategoriesList}>☰</button>
      </div>

      {/* Панель фильтров */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header">
            <h3>Фильтры</h3>
            <button className="btn-close" onClick={toggleFilters}>✕</button>
          </div>
          
          <div className="filters-content">
            {/* Фильтр по цвету */}
            {filters.colors.length > 0 && (
              <div className="filter-group">
                <h4>Цвет</h4>
                <div className="filter-options">
                  {filters.colors.map(color => (
                    <label key={color} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFilters.colors.includes(color)}
                        onChange={() => handleFilterChange('color', color)}
                      />
                      <span>{color}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Фильтр по размеру */}
            {filters.sizes.length > 0 && (
              <div className="filter-group">
                <h4>Размер</h4>
                <div className="filter-options">
                  {filters.sizes.map(size => (
                    <label key={size} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFilters.sizes.includes(size)}
                        onChange={() => handleFilterChange('size', size)}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Фильтр по цене */}
            <div className="filter-group">
              <h4>Цена</h4>
              <div className="price-range">
                <div className="price-input-group">
                  <label>От</label>
                  <input
                    type="number"
                    value={selectedFilters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    min={filters.priceRange.min}
                    max={selectedFilters.maxPrice}
                  />
                </div>
                <div className="price-input-group">
                  <label>До</label>
                  <input
                    type="number"
                    value={selectedFilters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    min={selectedFilters.minPrice}
                    max={filters.priceRange.max}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="filters-footer">
            <button className="btn-reset" onClick={resetFilters}>Сбросить</button>
            <button className="btn-apply" onClick={toggleFilters}>Применить</button>
          </div>
        </div>
      )}

      {/* Панель списка категорий */}
      {showCategoriesList && (
        <div className="categories-list-panel">
          <div className="categories-list-header">
            <h3>Все категории</h3>
            <button className="btn-close" onClick={toggleCategoriesList}>✕</button>
          </div>
          <div className="categories-list-content">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-list-item ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedCategory(cat.id)
                  setShowCategoriesList(false)
                  hapticFeedback('light')
                }}
              >
                <span className="category-icon-large">{cat.icon}</span>
                <span className="category-name-large">{cat.name}</span>
                {selectedCategory === cat.id && <span className="check-mark">✓</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      

      {/* Categories */}
      <div className="categories">
        <div className="categories-scroll">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`category-chip ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(cat.id)
                hapticFeedback('light')
              }}
              disabled={categoriesLoading && cat.id !== 'all'}
            >
              <span className="category-icon">{cat.icon}</span>
              <span className="category-name">{cat.name}</span>
            </button>
          ))}
        </div>
        {categoriesError && (
          <p className="categories-error">{categoriesError}</p>
        )}
      </div>

      {/* Admin Section - Category Management */}
      {canManageCategories && (
        <div className="admin-section">
          <button className="btn-manage-categories" onClick={handleCategoriesClick}>
            ⚙️ Управление категориями
          </button>
          <button 
            className="btn-manage-products" 
            onClick={() => {
              hapticFeedback('medium')
              navigate('/admin/products')
            }}
          >
            📦 Управление товарами
          </button>
        </div>
      )}

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>Товары не найдены</h3>
          <p>В этой категории пока нет товаров</p>
        </div>
      ) : (
        <div className="products-grid">
          {filteredProducts.map(product => (
            <div 
              key={product.id} 
              className="product-card fade-in"
              onClick={() => handleProductClick(product.id)}
            >
              <div className="product-image">
                <img 
                  src={getProductImage(product)}
                  alt={product.name}
                  loading="lazy"
                />
                {product.old_price && product.old_price > product.price && (
                  <div className="discount-badge">
                    -{Math.round((1 - product.price / product.old_price) * 100)}%
                  </div>
                )}
                {(() => {
                  // Показываем "New" для товаров, созданных менее 7 дней назад
                  if (product.created_at) {
                    const createdDate = new Date(product.created_at)
                    const daysDiff = (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
                    if (daysDiff <= 7) {
                      return <div className="new-badge">New</div>
                    }
                  }
                  return null
                })()}
                {product.in_stock === false && (
                  <div className="out-of-stock-badge">Нет в наличии</div>
                )}
              </div>
              
              <div className="product-info">
                <div className="product-meta">
                  <span className="product-category">{product.category}</span>
                </div>
                <h3 className="product-name">{product.name}</h3>
                <p className="product-description">{product.description}</p>
                
                <div className="product-footer">
                  <div className="price-container">
                    {product.old_price && product.old_price > product.price && (
                      <span className="product-old-price">{formatPrice(product.old_price)}</span>
                    )}
                    <span className="product-price">{formatPrice(product.price)}</span>
                  </div>
                  {product.sizes && (() => {
                    try {
                      const sizes = typeof product.sizes === 'string' ? JSON.parse(product.sizes) : product.sizes
                      return sizes && sizes.length > 0 ? (
                        <span className="product-sizes">
                          {sizes.length} размеров
                        </span>
                      ) : null
                    } catch (e) {
                      return null
                    }
                  })()}
                </div>

                <div className="product-actions">
                  <div className="product-stock">
                    {product.in_stock === false ? 'Нет в наличии' : 'В наличии'}
                  </div>
                  <button 
                    className="product-buy-btn"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (product.in_stock !== false) {
                        addToCart(product, 1)
                        hapticFeedback('success')
                      }
                    }}
                    disabled={product.in_stock === false}
                  >
                    В корзину
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}

export default Catalog
